import { QueryKey } from "@tanstack/react-query";

import type {
	CreateReviewCycleRequestInput,
	CreateReviewCycleResponseJson,
	ExportReviewCycleReviewReportsDetailsBody,
	ExportReviewCycleReviewReportsDetailsResponseJson,
	GetReviewCycleDetailsResponseFrontend,
	GetReviewCycleDetailsResponseJson,
	GetReviewCyclePreviewSubjectsResultsRequestInput,
	GetReviewCyclePreviewSubjectsResultsResponseJson,
	GetReviewCycleSubjectsResponseFrontend,
	GetReviewCyclesResponseJson,
	PostReviewCycleCalculateBodyFrontend,
	PostReviewCyclePreviewSubjectsRequestInput,
	PostReviewCyclePreviewSubjectsResponseJson,
	ReviewCycleStatus,
	ReviewCycleType,
	ReviewDetailsFrontend,
} from "@performyard/service-contracts";
import { JSON_STREAM_DELIMITER } from "@performyard/utils-isomorphic";

import { queryClient } from "../../../queryClient";
import { internalRequest, internalStreamRequest, processJsonStream } from "./internal-base-request";

// ============================================================================
// GET /v1/reviews/review-cycles
// ============================================================================

export type GetReviewCyclesParams = {
	search?: string;
	status?: ReviewCycleStatus[];
	type?: ReviewCycleType;
	easyAddEnabled?: boolean;
	sortName?: string;
	sortDirection?: "asc" | "desc";
	skip?: number;
	limit?: number;
};

export const getReviewCyclesList = async (params: GetReviewCyclesParams) => {
	const searchParams = new URLSearchParams();

	if (params.search) searchParams.set("search", params.search);
	if (params.status?.length) {
		for (const s of params.status) {
			searchParams.append("status", s);
		}
	}
	if (params.type) searchParams.set("type", params.type);
	if (params.easyAddEnabled !== undefined) searchParams.set("easyAddEnabled", String(params.easyAddEnabled));
	if (params.sortName) searchParams.set("sortName", params.sortName);
	if (params.sortDirection) searchParams.set("sortDirection", params.sortDirection);
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));

	const queryString = searchParams.toString();
	const url = `/v1/reviews/review-cycles${queryString ? `?${queryString}` : ""}`;

	const [data] = await internalRequest<GetReviewCyclesResponseJson>(url);
	return data;
};

export const calculateReviewCycleStatus = async (reviewCycleId: string) => {
	const body: PostReviewCycleCalculateBodyFrontend = {
		reviewCycleId,
		actions: ["status"],
	};

	const [response] = await internalRequest("/v1/review-cycle/calculate", {
		method: "POST",
		body: JSON.stringify(body),
	});

	return response;
};

export const reviewCycleSubjects = async (reviewCycleId: string) => {
	const [response] = await internalRequest<GetReviewCycleSubjectsResponseFrontend>(
		`/v1/review-cycle/${reviewCycleId}/subjects`,
		{
			method: "GET",
		}
	);

	return response;
};

// Track active streams per review cycle to prevent concurrent streams
const activeStreams = new Map<string, AbortController>();

export const reviewCycleReviewsDetailsStream = async (
	reviewCycleId: string,
	queryKey: QueryKey,
	onChunk?: (chunk: { data: ReviewDetailsFrontend[] }) => void
) => {
	// Abort any existing stream for this review cycle before starting a new one
	const existingController = activeStreams.get(reviewCycleId);
	if (existingController) {
		existingController.abort();
		activeStreams.delete(reviewCycleId);
	}

	// Create a new abort controller for this stream
	const abortController = new AbortController();
	activeStreams.set(reviewCycleId, abortController);

	// Initialize cache to empty array
	queryClient.setQueryData<ReviewDetailsFrontend[]>(queryKey, []);

	try {
		const response = await internalStreamRequest(`/v1/review-cycle/${reviewCycleId}/reviews/details`, {
			method: "GET",
			signal: abortController.signal,
		});

		if (response.status !== 200) {
			throw new Error(`Failed to get review cycle reviews details: ${response.statusText}`);
		}

		await processJsonStream<ReviewDetailsFrontend>({
			response,
			delimiter: JSON_STREAM_DELIMITER,
			signal: abortController.signal,
			onItem: (parsed) => {
				parsed.reviewDueDate = parsed.reviewDueDate ? new Date(parsed.reviewDueDate) : null;
				parsed.completeDate = parsed.completeDate ? new Date(parsed.completeDate) : null;
				return parsed;
			},
			onParsed: (parsedData) => {
				onChunk?.({ data: parsedData });
				queryClient.setQueryData(queryKey, (current: ReviewDetailsFrontend[]) => {
					return current ? [...current, ...parsedData] : parsedData;
				});
			},
		});

		// Return the final accumulated result
		return queryClient.getQueryData<ReviewDetailsFrontend[]>(queryKey) ?? [];
	} catch (error: any) {
		// Don't throw on abort - this is expected behavior
		if (error.name === "AbortError") {
			return [];
		}
		throw error;
	} finally {
		// Clean up the active stream tracking
		activeStreams.delete(reviewCycleId);
	}
};

export const deleteReviewFromReviewCycle = async (reviewCycleId: string, reviewId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/reviews/${reviewId}`, {
		method: "DELETE",
	});

	return response;
};

export const getReviewCycleDetails = async (reviewCycleID: string) => {
	const [data] = await internalRequest<GetReviewCycleDetailsResponseJson>(
		`/v1/review-cycle/${reviewCycleID}/details`
	);

	let parsedAutoflow: GetReviewCycleDetailsResponseFrontend["autoflow"] | null = null;
	if (data.autoflow?.trigger?.type === "scheduled") {
		// Convert strings to dates
		parsedAutoflow = {
			...data.autoflow,
			createdAt: data.autoflow.createdAt ? new Date(data.autoflow.createdAt) : undefined,
			deletedAt: data.autoflow.deletedAt ? new Date(data.autoflow.deletedAt) : undefined,
			updatedAt: data.autoflow.updatedAt ? new Date(data.autoflow.updatedAt) : undefined,
			trigger: {
				...data.autoflow.trigger,
				scheduled: {
					...data.autoflow.trigger.scheduled,
					inauguralTimeDate: data.autoflow.trigger.scheduled.inauguralTimeDate
						? new Date(data.autoflow.trigger.scheduled.inauguralTimeDate)
						: undefined,
					startTimeDate: data.autoflow.trigger.scheduled.startTimeDate
						? new Date(data.autoflow.trigger.scheduled.startTimeDate)
						: undefined,
					endTimeDate: data.autoflow.trigger.scheduled.endTimeDate
						? new Date(data.autoflow.trigger.scheduled.endTimeDate)
						: undefined,
				},
			},
		};
	}

	const parsedResp: GetReviewCycleDetailsResponseFrontend = {
		...data,
		startDate: new Date(data.startDate),
		dueDate: data.dueDate ? new Date(data.dueDate) : null,
		autoflow: parsedAutoflow,
	};

	return parsedResp;
};

export const closeReviewCycle = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/close`, {
		method: "POST",
	});

	return response;
};

export const bulkDeactivateEasyAddFormsForReviewCycle = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/easy-add-bulk-deactivate`, {
		method: "POST",
	});

	return response;
};

export const bulkApproveEasyAddFormsForReviewCycle = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/easy-add-bulk-approve`, {
		method: "POST",
	});

	return response;
};

export const deleteReviewCycle = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}`, {
		method: "DELETE",
	});

	return response;
};

export const downloadGoalDiscussionCsv = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/goal-discussion-csv-download`, {
		method: "GET",
	});

	return response;
};

export const getGoalDiscussionCsvDownloadStatus = async (jobId: string) => {
	const [response] = await internalRequest(`/v1/async-job/${jobId}/status`, {
		method: "GET",
	});

	return response;
};

export const createReviewCycle = async (
	body: CreateReviewCycleRequestInput
): Promise<CreateReviewCycleResponseJson> => {
	const [response] = await internalRequest<CreateReviewCycleResponseJson>(`/v1/reviews/review-cycles`, {
		method: "POST",
		body: JSON.stringify(body),
	});
	return response;
};

export const postReviewCyclePreviewSubjects = async (
	body: PostReviewCyclePreviewSubjectsRequestInput
): Promise<PostReviewCyclePreviewSubjectsResponseJson> => {
	const [response] = await internalRequest<PostReviewCyclePreviewSubjectsResponseJson>(
		`/v1/reviews/review-cycles/preview-subjects`,
		{
			method: "POST",
			body: JSON.stringify(body),
		}
	);
	return response;
};

export const getReviewCyclePreviewSubjectsResults = async (
	previewId: string,
	params: GetReviewCyclePreviewSubjectsResultsRequestInput
): Promise<GetReviewCyclePreviewSubjectsResultsResponseJson> => {
	const search = new URLSearchParams();
	if (params.search) search.set("search", params.search);
	if (params.sortName) search.set("sortName", params.sortName);
	if (params.sortDirection) search.set("sortDirection", params.sortDirection);
	if (params.skip !== undefined) search.set("skip", String(params.skip));
	if (params.limit !== undefined) search.set("limit", String(params.limit));

	const [response] = await internalRequest<GetReviewCyclePreviewSubjectsResultsResponseJson>(
		`/v1/reviews/review-cycles/preview-subjects/${previewId}/results?${search.toString()}`,
		{ method: "GET" }
	);
	return response;
};

export const exportReviewCycleReviewReportsDetailsCsv = async (
	reviewCycleId: string,
	body: ExportReviewCycleReviewReportsDetailsBody
) => {
	const [response] = await internalRequest<ExportReviewCycleReviewReportsDetailsResponseJson>(
		`/v1/review-cycle/${reviewCycleId}/review-reports/details/export-csv`,
		{
			method: "POST",
			body: JSON.stringify(body),
		}
	);

	return response;
};
